/**
 * Presets de endpoints por cargo (modal Grupos → «Por cargo»).
 * Um clique inclui todos os endpoints do cargo ainda não atribuídos ao grupo.
 *
 * Fonte única também para migrações: ver `migrationGroups` + `doc/migrations/MAPA-CARGOS-PERMISSOES.md`.
 *
 * Campos:
 * - patterns: substring em path (`endpoint`) ou nome — inclui (qualquer método)
 * - getPatterns: mesma lógica, somente GET (consultas)
 * - excludePatterns: remove matches (útil em all / pacotes amplos)
 *   Prefixo `^` = só path exato ou path que começa com o prefixo + `/`
 *   (evita `/auth` excluir `/integrations/ceaf/auth`)
 *   Prefixo `=` = path exatamente igual (ex.: `=/companies`)
 * - all: true → todos os endpoints (respeitando excludePatterns)
 * - migrationGroups: nomes em `groups.name` para SQL de permissão (primary + aliases legados)
 */
const cargoPresets = [
	{
		label: 'Separador',
		hint: 'Somente picking (web + coletor): separação, caixas, picklists e etiquetas de volume/stage.',
		migrationGroups: [
			'Separador',
			'Operadores de Saída',
			'Op. Empilhadeira',
		],
		patterns: [
			'/output-picking',
			'output-picking-boxes',
			'countback',
			'print-picklists',
			'print-picklist-order',
			'print-pick-control',
			'print-pallets-overturn',
			'picking-waves',
			'print-stage-labels',
			'list-stage-labels',
			'list-volumes',
			'volume-labels',
			'print-labels',
			'picked-by',
			'outputs/search',
			'/outputs/list-',
		],
		getPatterns: [
			'/outputs',
			'/output-orders',
			'/output-picking',
			'output-picking-boxes',
		],
		excludePatterns: [
			'conference',
			'conferenc',
			'create-invoices',
			'reverse',
			'unpick-approve',
			'unpick-reject',
			'unpick-accept',
			'unpick-treat-reject',
			'unpick-substitute',
			'unpick-treatment',
			'manual-status',
			'approve-orders',
			'reject-orders',
			'input-orders/approve-batch',
			'input-orders/reject-batch',
			'dock-expedition/advance',
			'print-conference',
			'print-aviso',
			'print-romaneio',
			'print-iqvia',
			'print-details',
			'print-balance-trace',
			'delete-separate',
			'repair-',
		],
	},
	{
		label: 'Conferente',
		hint: 'Conferência de entrada e saída (web + coletor), checkpoints e divergências.',
		migrationGroups: [
			'Conferente',
			'Operadores de Entrada',
		],
		patterns: [
			'output-conference',
			'/conference',
			'collector-conference',
			'print-conference',
			'divergences',
			'/inputs/',
			'/inputs',
			'inputs-complete',
			'inputs-coletor',
			'inputs/<input_id>/inventory',
			'putaway',
			'input-rastro',
			'verified-at',
			'resolve-gtin',
			'checked-by',
		],
		getPatterns: [
			'/outputs',
			'/carrier-volumes',
			'/output-orders',
			'/inputs',
			'/input-orders',
			'/products',
			'/lots',
			'/stocks',
		],
		excludePatterns: [
			'/output-picking',
			'print-picklists',
			'print-picklist-order',
			'reverse',
			'unpick-approve',
			'unpick-accept',
			'unpick-treat-reject',
			'unpick-substitute',
			'unpick-treatment',
			'approve-orders',
			'reject-orders',
			'input-orders/approve-batch',
			'input-orders/reject-batch',
			'manual-status',
			'create-invoices',
			'/collector/inventory',
			'/inventory-sessions',
			'bulk-block',
			'bulk-unblock',
		],
	},
	{
		label: 'Auditor/Inventário',
		hint: 'Inventário V2 (web + coletor), auditoria de estoque/reserva e ACTION audit:*.',
		migrationGroups: [
			'Auditor/Inventário',
			'Operadores de Inventário',
			'Gestores de Inventário',
			'Inventário - Contagem',
			'Inventário - Gestor',
			'SUPERVISOR_AUDITORIA',
		],
		patterns: [
			'/inventory-sessions',
			'/collector/inventory',
			'/inventories',
			'/audit/',
			'audit:',
			'audit-pending',
			'audit-report',
			'bi-audit',
			'/stock/reservation-link',
			'/stock/release-orphan',
			'/stock/negative-balance',
			'print-divergences',
			'print-progress',
			'print-simulation',
			'print-closure',
			'simulate-close',
			'/close/start',
			'/close/tick',
			'/close/status',
			'unfreeze',
			'clear-counts',
			'finalize-found',
			'close-preview',
			'confirm-shortage',
			'location-progress',
			'complete-preview',
			'/locations/complete',
			'/locations/close',
		],
	},
	{
		label: 'Analista Operação',
		hint: 'Operação do dia a dia no SIAL web (sem aprovar, estornar, params ou integrações).',
		migrationGroups: [
			'Analista Operação',
			'Gestores de Armazém',
			'Operadores de Saída',
			'Operadores de Pedido de Saída',
		],
		patterns: [
			'/outputs',
			'/carrier-volumes',
			'/output-orders',
			'/delivery-locations',
			'/carrier-boxes',
			'/output-picking',
			'/output-unpick',
			'/output-conference',
			'/inputs',
			'/input-orders',
			'/receipt-types',
			'/stocks',
			'/stock-management',
			'/transactions',
			'/movements',
			'movement-kind',
			'movement-control',
			'/lots',
			'/warehouses',
			'/locations',
			'/replenishment',
			'/devices',
			'/customers',
			'/customer-routes',
			'/customer-delivery',
			'/products',
			'/packagings',
			'/product-packagings',
			'print-',
			'resolve-gtin',
			'putaway',
			'order-upload',
			'import-xml',
			'search-references',
			'espelho-pedido',
			'export-list-csv',
			'/outputs/export-csv',
			'/inputs/export-csv',
			'expected-delivery',
			'create-pending-order',
			'shortage-link',
			'dock-expedition',
			'input-dock-schedules',
			'picking-waves',
			'collector-conference',
			'unpick-request',
			'location-move',
			'locations-catalog',
			'sku-no-output',
			'expiring-items',
			'output-purchase',
			'storage-class',
			'stock-packaging',
			'balance-blocks',
			'print-balance',
			// logística / SESSP operação
			'/shipments',
			'/vehicles',
			'/drivers',
			'/cte-documents',
			'/delivery-proofs',
			'/returns',
			'/exchange-letters',
			'/sessp/drs-regions',
			'/sessp/sla-rules',
			'/sla-rules',
			'/transport-events',
		],
		getPatterns: [
			'=/companies',
		],
		excludePatterns: [
			'approve-orders',
			'reject-orders',
			'input-orders/approve-batch',
			'input-orders/reject-batch',
			'reverse',
			'unpick-approve',
			'unpick-reject',
			'unpick-accept',
			'unpick-treat-reject',
			'unpick-substitute',
			'unpick-treatment',
			'manual-status-advance',
			'dock-expedition/advance',
			'bulk-block',
			'bulk-unblock',
			'^/endpoints',
			'^/auth',
			'^/groups',
			'^/users',
			'/techhub',
			'/gsnet',
			'/amil/',
			'vendatus',
			'iqvia',
			'closeup',
			'forced-pull',
			'forced-flush',
			'/integration-events',
			'/integrations/ceaf',
			'/inventory-sessions',
			'/collector/inventory',
			'/audit/',
			'audit:',
			'/invoices',
			'/invoice-lots',
			'nfe-emission',
			'inutilizar',
			'create-invoices',
			'/reports/',
			'kardex',
			'bi-',
			'/bi/',
			'/import-center',
			'/logs',
			'/version',
			'/sessp/billing',
			'/billing-tariffs',
			'/billing-periods',
		],
	},
	{
		label: 'Gestor Operação',
		hint: 'Operação + supervisão: aprovar, estornar, doca, bloqueios, reparos e exceções.',
		migrationGroups: [
			'Gestor Operação',
			'Gestores de Armazém',
		],
		patterns: [
			'/outputs',
			'/carrier-volumes',
			'/output-orders',
			'/delivery-locations',
			'/carrier-boxes',
			'/output-picking',
			'/output-unpick',
			'/output-conference',
			'/inputs',
			'/input-orders',
			'/receipt-types',
			'/stocks',
			'/stock-management',
			'/transactions',
			'/movements',
			'movement-kind',
			'movement-control',
			'/lots',
			'/warehouses',
			'/locations',
			'/replenishment',
			'/devices',
			'/customers',
			'/customer-routes',
			'/customer-delivery',
			'/products',
			'/packagings',
			'/product-packagings',
			'print-',
			'resolve-gtin',
			'putaway',
			'order-upload',
			'import-xml',
			'search-references',
			'espelho-pedido',
			'export-list-csv',
			'/outputs/export-csv',
			'/inputs/export-csv',
			'expected-delivery',
			'create-pending-order',
			'shortage-link',
			'dock-expedition',
			'input-dock-schedules',
			'picking-waves',
			'collector-conference',
			'unpick-request',
			'unpick-approve',
			'unpick-reject',
			'unpick-accept',
			'unpick-treat-reject',
			'unpick-substitute',
			'unpick-treatment',
			'approve-orders',
			'reject-orders',
			'input-orders/approve-batch',
			'input-orders/reject-batch',
			'reverse',
			'manual-status-advance',
			'repair-',
			'delete-separate',
			'bulk-block',
			'bulk-unblock',
			'bulk-move',
			'batch-patch',
			'change-packaging',
			'split-stock',
			'unitarize',
			'lot-meta-preview',
			'location-move',
			'locations-catalog',
			'sku-no-output',
			'expiring-items',
			'output-purchase',
			'storage-class',
			'stock-packaging',
			'balance-blocks',
			'/audit/approve',
			'audit:release_block',
			'audit:release_reservation',
			'audit:approve_bulk',
			'/stock/reservation-link',
			'/stock/release-orphan',
			'/stock/negative-balance',
			'/shipments',
			'/vehicles',
			'/drivers',
			'/cte-documents',
			'/delivery-proofs',
			'/returns',
			'/exchange-letters',
			'/sessp/drs-regions',
			'/sessp/sla-rules',
			'/sla-rules',
			'/transport-events',
		],
		getPatterns: [
			'=/companies',
		],
		excludePatterns: [
			'^/endpoints',
			'^/auth',
			'^/groups',
			'^/users',
			'/techhub',
			'/gsnet',
			'/amil/',
			'vendatus',
			'iqvia',
			'closeup',
			'forced-pull',
			'forced-flush',
			'/integration-events',
			'/integrations/ceaf',
			'/inventory-sessions',
			'/collector/inventory',
			'/invoices',
			'/invoice-lots',
			'nfe-emission',
			'inutilizar',
			'create-invoices',
			'/reports/',
			'kardex',
			'bi-',
			'/bi/',
			'/import-center',
			'/sessp/billing',
			'/billing-tariffs',
			'/billing-periods',
		],
	},
	{
		label: 'Analista Faturamento',
		hint: '100% do módulo de faturamento (NFs) + consultas gerais operacionais (GET).',
		migrationGroups: [
			'Analista Faturamento',
			'Administrativo',
		],
		patterns: [
			'/invoices',
			'/invoice-lots',
			'nfe-emission',
			'inutilizar',
			'create-invoices',
			'create-lots',
			'create-archive',
			'update-xml',
			'print-iqvia',
			'/cfops',
			'/sessp/billing',
			'/billing-tariffs',
			'/billing-periods',
		],
		getPatterns: [
			'/outputs',
			'/carrier-volumes',
			'/output-orders',
			'/inputs',
			'/input-orders',
			'/stocks',
			'/lots',
			'/products',
			'/class-tribs',
			'/customers',
			'/suppliers',
			'/carriers',
			'/delivery-locations',
			'/carrier-boxes',
			'/warehouses',
			'/locations',
			'/programs',
			'/payment-terms',
			'/price-lists',
			'/customer-prices',
			'/reports/',
			'=/companies',
		],
	},
	{
		label: 'Analista Cadastro',
		hint: 'Cadastros e consultas de catálogo, comercial, organização e armazenamento (sem inventário/params).',
		migrationGroups: [
			'Analista Cadastro',
		],
		patterns: [
			'/products',
			'/packagings',
			'/product-packagings',
			'/categories',
			'/manufacturers',
			'/units',
			'/classes',
			'/programs',
			'/customers',
			'/customer-routes',
			'/customer-prices',
			'/customer-delivery',
			'/payment-terms',
			'/price-lists',
			'/cfops',
			'/suppliers',
			'/carriers',
			'/delivery-locations',
			'/carrier-boxes',
			'/warehouses',
			'/locations',
			'/lots',
			'/receipt-types',
			'/input-docks',
			'/devices',
			'/replenishment',
			'count-report',
			'print-report',
			'locations-catalog',
			'resolve-gtin',
			'expiring-items',
			'/import-center',
			'/states',
			'/cities',
			'=/companies',
			'/sessp/drs-regions',
			'/sessp/sla-rules',
			'/sla-rules',
		],
		excludePatterns: [
			'/inventory-sessions',
			'/collector/inventory',
			'/inventories',
			'/audit/',
			'audit:',
			'^/endpoints',
			'^/auth',
			'^/groups',
			'^/users',
			'/techhub',
			'/gsnet',
			'/amil/',
			'/invoices',
			'/invoice-lots',
			'bulk-block',
			'bulk-unblock',
			'reverse',
			'approve-orders',
			'reject-orders',
			'input-orders/approve-batch',
			'input-orders/reject-batch',
		],
	},
	{
		label: 'Analista BI',
		hint: 'Todas as funções de BI e relatórios analíticos.',
		migrationGroups: [
			'Analista BI',
			'Bi',
		],
		patterns: [
			'/reports/',
			'/companies/<company_id>/reports',
			'kardex',
			'stock-complete',
			'stock-blocks-descriptive',
			'sku-extract',
			'output-lines',
			'output-productivity',
			'audit-report',
			'products-general',
			'product-packagings',
			'movement-control',
			'output-purchase',
			'expiring-items',
			'locations-catalog',
			'bi-',
			'/bi/',
			'nfe-emission-control',
			'ceaf-stock',
			'input-documents',
			'inputs-complete',
		],
	},
	{
		label: 'Analista Integração',
		hint: 'TechHub, GSNET, AMIL, Vendatus, IQVIA, Close-Up e eventos de integração.',
		migrationGroups: [
			'Analista Integração',
			'IBL Tech Sistemas',
			'Sessp',
		],
		patterns: [
			'/integration-events',
			'/integrations/',
			'/techhub',
			'/gsnet',
			'/amil/',
			'amil-',
			'iqvia',
			'closeup',
			'vendatus',
			'entity-alerts',
			'forced-pull',
			'forced-flush',
			'print-iqvia',
			'/integrations/ceaf',
		],
	},
	{
		label: 'Analista Gente',
		hint: 'Manutenção de usuários e grupos.',
		migrationGroups: [
			'Analista Gente',
		],
		patterns: [
			'^/users',
			'^/groups',
		],
		excludePatterns: [
			'^/endpoints',
			'^/auth',
			'/import-center',
			'/integration-events',
			'/techhub',
			'/logs',
		],
	},
	{
		label: 'Gerente',
		hint: 'Quase tudo no SIAL, exceto parâmetros sistêmicos (endpoints e auth). Inclui usuários, grupos, BI e integrações.',
		migrationGroups: [
			'Gerente',
		],
		all: true,
		excludePatterns: [
			'^/endpoints',
			'^/auth',
		],
	},
	{
		label: 'Administrador',
		hint: 'Tudo que existe no SIAL.',
		migrationGroups: [
			'Administrador',
			'Administradores',
		],
		all: true,
	},
];

export default cargoPresets;

/**
 * @param {{ endpoint?: string, name?: string, method?: string }} ep
 * @param {string} pat
 */
function _matchesPat(ep, pat)
{
	const path = String(ep?.endpoint || '').toLowerCase();
	const name = String(ep?.name || '').toLowerCase();
	let p = String(pat || '').toLowerCase();
	if (!p)
		return false;

	// Prefixo de path estrito: ^/auth casa /auth e /auth/login, mas NÃO /integrations/ceaf/auth
	if (p.startsWith('^'))
	{
		const prefix = p.slice(1);
		if (!prefix)
			return false;
		return path === prefix || path.startsWith(`${prefix}/`);
	}

	// Path exatamente igual
	if (p.startsWith('='))
	{
		const exact = p.slice(1);
		return path === exact;
	}

	return path.includes(p) || name.includes(p.replace(/\//g, ' ').trim());
}

/**
 * @param {{ endpoint?: string, name?: string, method?: string }} ep
 * @param {{ patterns?: string[], getPatterns?: string[], excludePatterns?: string[], all?: boolean }} functionGroup
 */
export function endpointMatchesFunctionGroup(ep, functionGroup)
{
	const exclude = functionGroup.excludePatterns || [];
	if (exclude.some((pat) => _matchesPat(ep, pat)))
		return false;

	if (functionGroup.all)
		return true;

	const patterns = functionGroup.patterns || [];
	if (patterns.some((pat) => _matchesPat(ep, pat)))
		return true;

	const method = String(ep?.method || '').toUpperCase();
	if (method === 'GET')
	{
		const getPatterns = functionGroup.getPatterns || [];
		if (getPatterns.some((pat) => _matchesPat(ep, pat)))
			return true;
	}

	return false;
}

/**
 * Mapa cargo → nomes de grupo no banco (para seeds SQL de permissão).
 * Fonte: campo `migrationGroups` de cada cargo neste arquivo.
 * @returns {{ label: string, groups: string[], hint: string }[]}
 */
export function getCargoMigrationMap()
{
	return cargoPresets.map((g) => ({
		label: g.label,
		groups: [...(g.migrationGroups || [g.label])],
		hint: g.hint || '',
	}));
}

/**
 * Lista SQL pronta para `WHERE g.name IN (...)` de um ou mais cargos (+ Administradores).
 * @param {string|string[]} cargoLabels
 * @param {{ includeAdmin?: boolean }} [opts]
 */
export function sqlGroupNamesForCargos(cargoLabels, opts = {})
{
	const labels = Array.isArray(cargoLabels) ? cargoLabels : [cargoLabels];
	const names = new Set();
	for (const label of labels)
	{
		const g = cargoPresets.find((x) => x.label === label);
		for (const n of (g?.migrationGroups || [label]))
			names.add(n);
	}
	if (opts.includeAdmin !== false)
	{
		names.add('Administradores');
		names.add('Administrador');
	}
	return [...names];
}
